import type {
  CountdownDisplayUnit,
  CountdownEditRequest,
  CountdownType,
} from './countdown-config';

import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FiInfo } from 'react-icons/fi';

import { CenteredDialogContent } from '#components/centered-dialog-content/centered-dialog-content';
import { toaster } from '#components/toaster/toaster';
import { Tooltip } from '#components/tooltip/tooltip';

import {
  countdownIsoToLocalInputValue,
  countdownLocalInputValueToIso,
  isValidRecurringCountdownCron,
} from './countdown-config';

export interface CountdownEditModalProps {
  request: CountdownEditRequest | null;
  onDismiss: () => void;
}

const DISPLAY_UNIT_OPTIONS: Array<{
  label: string;
  value: CountdownDisplayUnit;
}> = [
  { label: 'Auto', value: 'auto' },
  { label: 'Minutes', value: 'minutes' },
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
  { label: 'Weeks', value: 'weeks' },
];
const COUNTDOWN_TYPE_OPTIONS: Array<{
  label: string;
  value: CountdownType;
}> = [
  { label: 'Target', value: 'target' },
  { label: 'Recurring', value: 'recurring' },
];

export function CountdownEditModal({
  request,
  onDismiss,
}: CountdownEditModalProps) {
  const open = request != null;
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<CountdownType>('target');
  const [targetAt, setTargetAt] = useState('');
  const [cron, setCron] = useState('');
  const [displayUnit, setDisplayUnit] = useState<CountdownDisplayUnit>('auto');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!request) {
      setSaving(false);
      return;
    }

    setName(request.initialValues.name);
    setType(request.initialValues.type);
    setTargetAt(countdownIsoToLocalInputValue(request.initialValues.targetAt));
    setCron(request.initialValues.cron);
    setDisplayUnit(request.initialValues.displayUnit);
    queueMicrotask(() => nameRef.current?.focus());
  }, [request]);

  const handleClose = () => {
    if (saving) {
      return;
    }

    onDismiss();
  };

  const handleSave = async () => {
    if (!request) {
      return;
    }

    const nextName = name.trim();
    if (!nextName) {
      toaster.create({
        title: 'Missing countdown name',
        description: 'Enter a short name before saving the countdown.',
        type: 'warning',
      });
      return;
    }

    const nextTargetAt =
      type === 'target' ? countdownLocalInputValueToIso(targetAt) : '';
    if (type === 'target' && !nextTargetAt) {
      toaster.create({
        title: 'Invalid target date',
        description: 'Enter a valid target date and time before saving.',
        type: 'warning',
      });
      return;
    }

    const nextCron = cron.trim();
    if (type === 'recurring' && !nextCron) {
      toaster.create({
        title: 'Invalid recurring schedule',
        description: 'Enter a cron expression before saving.',
        type: 'warning',
      });
      return;
    }

    if (type === 'recurring' && !isValidRecurringCountdownCron(nextCron)) {
      toaster.create({
        title: 'Invalid recurring schedule',
        description: 'Enter a valid Croner cron expression before saving.',
        type: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      await request.onCommit({
        name: nextName,
        type,
        targetAt: nextTargetAt ?? '',
        cron: nextCron,
        displayUnit,
      });
      onDismiss();
    } catch (error) {
      toaster.create({
        title: 'Could not save countdown',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="lg"
    >
      <CenteredDialogContent maxW="min(90vw, 32rem)">
        <DialogHeader>
          <DialogTitle>
            {request?.mode === 'insert' ? 'Insert countdown' : 'Edit countdown'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Set a short label and display unit. Target countdowns use a fixed
              date and time, while recurring countdowns use a UTC cron schedule
              and count to the next occurrence.
            </Text>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                Name
              </Text>
              <Input
                ref={nameRef}
                type="text"
                placeholder="Launch"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
                autoComplete="off"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                Type
              </Text>
              <NativeSelect.Root disabled={saving}>
                <NativeSelect.Field
                  value={type}
                  onChange={(e) => setType(e.target.value as CountdownType)}
                >
                  {COUNTDOWN_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            </label>
            {type === 'target' ? (
              <label
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  Target date and time
                </Text>
                <Input
                  type="datetime-local"
                  value={targetAt}
                  onChange={(e) => setTargetAt(e.target.value)}
                  disabled={saving}
                />
              </label>
            ) : (
              <label
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <HStack gap={1} align="center">
                  <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                    Cron schedule
                  </Text>
                  <Tooltip
                    showArrow
                    positioning={{ placement: 'top' }}
                    contentProps={{
                      bg: 'bg.panel',
                      color: 'fg',
                      borderWidth: '1px',
                      borderColor: 'border.subtle',
                      boxShadow: 'lg',
                    }}
                    content={
                      <VStack align="start" gap={1} maxW="18rem">
                        <Text fontSize="xs" fontWeight="semibold">
                          Cron format
                        </Text>
                        <Text fontSize="xs">
                          `minute hour day-of-month month day-of-week`
                        </Text>
                        <Text fontSize="xs">
                          Example: `0 14 * * 1-5` means 14:00 UTC on weekdays.
                        </Text>
                        <Text fontSize="xs">
                          `*` means any value, and ranges like `1-5` are
                          allowed.
                        </Text>
                      </VStack>
                    }
                  >
                    <Box
                      as="span"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      color="fg.muted"
                      aria-label="Explain cron schedule format"
                    >
                      <FiInfo size={14} aria-hidden />
                    </Box>
                  </Tooltip>
                </HStack>
                <Input
                  type="text"
                  value={cron}
                  onChange={(e) => setCron(e.target.value)}
                  placeholder="0 14 * * 1-5"
                  disabled={saving}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <Text fontSize="xs" color="fg.muted">
                  Evaluated in UTC using Croner syntax.
                </Text>
              </label>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                Display unit
              </Text>
              <NativeSelect.Root disabled={saving}>
                <NativeSelect.Field
                  value={displayUnit}
                  onChange={(e) =>
                    setDisplayUnit(e.target.value as CountdownDisplayUnit)
                  }
                >
                  {DISPLAY_UNIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            </label>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button colorPalette="blue" onClick={handleSave} disabled={saving}>
              Save countdown
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
